import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTasks } from '../../api/hooks';
import {
  FIRE_MODE_HOLD_COPY,
  isFireModeError,
  useDeleteSchedule,
  useOverrideOccurrence,
  useRefreshSchedules,
  useScheduleOccurrences,
  useSchedules,
  useScheduleUpdatePreview,
  useSetScheduleEnabled,
  useSkipOccurrence,
  useUpdateSchedule,
  type OccurrenceWindow,
} from '../../api/hooks/useSchedules';
import { DataTable, type DataTableSort } from '../../design-system/components/DataTable';
import { AlertBanner } from '../../design-system/components/AlertBanner';
import { SkeletonLoader } from '../../design-system/components/SkeletonLoader';
import { RoleGuard } from '../../auth/RoleGuard';
import { audienceFor } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import { usePreviewRole } from '../../auth/useRolePreview';
import { previewRoleLabel } from '../../auth/role-preview';
import { MAINTENANCE_MANAGE_ROLES, TASK_DISPATCH_ROLES } from '../../auth/types';
import { useTaskDispatchGate } from './useTaskDispatchGate';
import { useActiveSite } from '../../state/useActiveSite';
import { scopeByActiveSite } from '../../api/scope';
import type { TaskState } from '../../api/rmf-client';
import type { ScheduleOccurrence, TaskSchedule, UpdateTaskScheduleRequest } from '../../api/v1';
import {
  ALERT_DEEP_LINK_EVENT,
  taskForDeepLink,
  type AlertDeepLinkKind,
} from '../../state/alert-deep-link';
import { TaskDispatchForm } from './TaskDispatchForm';
import { TaskDetailDrawer } from './TaskDetailDrawer';
import { NewTaskDrawer } from './NewTaskDrawer';
import { ScheduleEditDrawer } from './ScheduleEditDrawer';
import { ActionToast } from './ActionToast';
import type { EditScheduleRequest } from './calendar/LanePanel';
import { clippedText, droppedText, keptText, runText } from './schedule-edit-summary';
import {
  endText,
  keptOldTime,
  newTimeAfter,
  newTimeAfterText,
  stillToRunText,
} from './calendar/schedule-span';
import {
  batchFailureText,
  eachInTurn,
  toggleFailureWords,
  type BatchOutcome,
  type PlanVerb,
} from './calendar/plan-batch';
import { toggledWords, type ToggleOutcome } from './calendar/useScheduleActions';
import { TaskCalendar, type CalendarSlot } from './TaskCalendar';
import { CalendarLanesContainer } from './calendar/CalendarLanesContainer';
import { PhoneCalendarContainer } from './calendar/PhoneCalendarContainer';
import { PHONE_QUERY, phoneDateLine } from './calendar/phone-model';
import { parsePhoneUrl, writePhoneUrl } from './calendar/phone-url';
import { useMinuteNow } from './calendar/useMinuteNow';
import { useTodayKey } from './calendar/useTodayKey';
import { TasksPhoneTabs } from './TasksPhoneTabs';
import { tasksPhonePanelId, tasksPhoneTabId, type TasksPhoneTab } from './tasks-phone-tab';
import { useMediaQuery } from '../../state/useMediaQuery';
import {
  parseCalendarUrl,
  writeCalendarUrl,
  type CalendarUrlState,
} from './calendar/calendar-url';
import { addDays, dayWindow, weekWindow, type DateKey } from './calendar/lane-time';
import { loadCalendarView, saveCalendarView } from './calendar/calendar-prefs';
import { agendaWindow } from './calendar/agenda-model';
import { monthWindow } from './calendar/month-model';
import type { LaneItem } from './calendar/lane-items';
import type { BlockTimePreset } from './calendar/BlockTimeDialog';
import { TaskControlActions } from './TaskControlActions';
import { adaptCompletion, type OlderTask } from './older-tasks';
import { OccurrenceScopeDialog, type OccurrenceScope } from './OccurrenceScopeDialog';
import { describeRule, isOneShotRule, shiftFollowingRuns } from './rrule';
import {
  useCreateMaintenanceWindow,
  useDeleteMaintenanceWindow,
  useMaintenanceWindows,
  windowCovering,
} from '../../api/hooks/useMaintenance';
import { resolveSiteTimeZone, siteTimeZoneWarning } from './site-timezone';
import { formatZonedClock, formatZonedDateTime, timeZoneLabel } from './zoned-time';
import { TaskModal } from './TaskModal';
import { formatDateTime } from '../../utils/date-format';
import { phaseLabel } from './task-phase-label';
import './TasksScreen.css';
import { NoSiteNotice } from '../../shell/NoSiteNotice';
import { HelpScreenNote } from '../../help';
import { useUserPreferences } from '../../state/useUserPreferences';
import {
  isDefaultLayout,
  layoutToSave,
  moveColumn,
  resolveTableLayout,
  setColumnVisible,
  type ResolvedColumn,
} from '../../state/table-layout';
import {
  readCachedTaskRequest,
  useTaskRequestCacheVersion,
} from '../../api/hooks/task-request-cache';
import { CLEAN_MODES, liveCleanMode, taskTypeKey, typeKeyText } from './clean-modes';
import {
  OLDER_FINISHED_COLUMN,
  TASK_COLUMN_SPECS,
  olderTaskColumnDefs,
  taskColumnDefs,
  triggeredByLabel,
  type TaskColumnId,
} from './task-columns';
import { TaskColumnsControl } from './TaskColumnsControl';
import { locationFromPhases, locationFromRequest } from './task-location';
import { useOlderTasks, useOlderTasksGap } from '../../api/hooks/useOlderTasks';
import { olderRowsFrom, olderStartBeforeMs } from './older-tasks';
import { OlderTasksSection } from './OlderTasksSection';
import { OlderTaskDrawer } from './OlderTaskDrawer';
import { useTaskDistances } from '../../api/task-distances';
import { useTaskDistanceCatchUp } from '../../api/hooks/useTaskDistanceCatchUp';
import { TASK_CSV_DISTANCE_FIELDS, distanceCsvValues } from './task-distance';


/**
 * Newest submission on top. The feed arrives in store order, which is
 * roughly insertion order with every live update appended — so before
 * this the newest task sat at the bottom of a list that grows by the
 * day, and the row an operator had just dispatched was the one they
 * had to scroll for. `submittedAtMs` unset sorts LAST under `desc`
 * (see the column's `sortValue`), so a record with no receive time
 * never masquerades as the newest.
 */
const TASKS_DEFAULT_SORT: DataTableSort = { columnId: 'submitted', direction: 'desc' };

/** The older list's order: the order its pages arrive in. See
 *  `OLDER_FINISHED_COLUMN`. */
const OLDER_TASKS_DEFAULT_SORT: DataTableSort = { columnId: 'finished', direction: 'desc' };

/** The saved-layout key for this table (`UserPreferences.tableLayouts`). */
const LAYOUT_KEY = 'tasks';

/** "A platform admin, site supervisor or FOH operator": the roles that may
 *  create tasks, by name, for the "Dispatch unavailable" banner. */
const DISPATCH_ROLES_TEXT = (() => {
  const names = TASK_DISPATCH_ROLES.map((r) =>
    previewRoleLabel(r).replace(/^[A-Z](?=[a-z])/, (c) => c.toLowerCase()),
  );
  return `A ${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
})();

/** Same order as `TASKS_DEFAULT_SORT`, for the CSV export -- and for
 *  the rows handed to the live table, see `tableRows`. The older table
 *  has its own order, see `OLDER_TASKS_DEFAULT_SORT`. */
function bySubmittedNewestFirst(
  a: { submittedAtMs?: number },
  b: { submittedAtMs?: number },
): number {
  return (b.submittedAtMs ?? -1) - (a.submittedAtMs ?? -1);
}

/** What the filter row reads off a row; a live task and an older one
 *  both have it, so one predicate serves both lists. */
interface FilterableTask {
  id: string;
  status: string;
  type: string;
  assignedRobot?: string;
  triggeredBy?: string;
}

function matchesTaskFilters(
  t: FilterableTask,
  status: 'all' | TaskState['status'],
  type: 'all' | string,
  search: string,
  /** The row's TYPE KEYS (`clean-modes.ts::taskTypeKey`): one, except a
   *  clean whose mode is not known yet, which matches all three modes
   *  rather than being hidden under a guess. */
  typeKeys: readonly string[],
): boolean {
  if (status !== 'all' && t.status !== status) return false;
  if (type !== 'all' && !typeKeys.includes(type)) return false;
  const q = search.trim().toLowerCase();
  if (q) {
    const haystack = [t.id, t.assignedRobot ?? '', triggeredByLabel(t.triggeredBy).text]
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  return true;
}

/**
 * Per-category waypoint columns for CSV export. `task.phases` is the
 * ordered, RMF-confirmed "places actually visited" record (same data
 * `TaskPhasesList` renders on-screen — reusing its `phaseLabel` so the
 * CSV never disagrees with what the drawer shows). Column *meaning*
 * differs by category rather than dumping one ambiguous "waypoints"
 * list for everything: `buildPhases` in `TaskDispatchForm.tsx`
 * confirms station/delivery/patrol/clean each construct `phases` in a
 * fixed, known order (station: [destination]; delivery: [pickup,
 * dropoff]; patrol: every waypoint in visit order; clean: a single
 * `perform_action` phase for the zone) — RMF executes phases in the
 * order given, so indexing by position here is safe.
 */
function waypointColumns(t: TaskState): {
  destination: string;
  pickup: string;
  dropoff: string;
  waypoints: string;
  cleaning_area: string;
} {
  const labels = (t.phases ?? []).map(phaseLabel);
  const out = { destination: '', pickup: '', dropoff: '', waypoints: '', cleaning_area: '' };
  if (t.type === 'station_docking') {
    out.destination = labels[0] ?? '';
  } else if (t.type === 'delivery') {
    out.pickup = labels[0] ?? '';
    out.dropoff = labels.length > 1 ? labels[1]! : '';
  } else if (t.type === 'clean') {
    out.cleaning_area = labels[0] ?? '';
  } else {
    // patrol, and anything else — the generic ordered list. `charge`
    // lands here too: RMF builds its own charge tasks and this screen
    // has no builder to read a fixed phase order out of, so the
    // honest column is the ordered list of whatever phases arrived.
    out.waypoints = labels.join(' → ');
  }
  return out;
}

export function TasksScreen() {
  const tasks = useTasks();
  const { site, sites } = useActiveSite();
  // The clock every schedule on this screen is written and read in. It
  // is the SITE's, not the browser's — see `site-timezone.ts`.
  const siteZone = useMemo(() => resolveSiteTimeZone(sites, site?.id), [sites, site?.id]);
  const zoneWarning = siteTimeZoneWarning(siteZone);
  /** Feature flag + backend readiness + `TASK_DISPATCH_ROLES`, shared
   *  through `useTaskDispatchGate` so this screen and the Overview
   *  map's click-a-zone dispatch can never disagree about who may
   *  dispatch. `roleAllowed` comes back separate from `writeDisabled`
   *  so the banner below can say *why* dispatch is unavailable. */
  const { writeDisabled, roleAllowed, role, dispatchFlagEnabled, readiness, readinessDetail } =
    useTaskDispatchGate();
  /** Who reads the dispatch banner's words: the flag name and the
   *  readiness reading are ops' only (P9, 2026-10-31). */
  const audience = audienceFor(useAuth().roles);

  // VIEW, CALENDAR VIEW AND DAY LIVE IN THE URL (`calendar/calendar-url.ts`),
  // so a link to a robot's day can be shared and Back undoes a drill-down.
  const [searchParams, setSearchParams] = useSearchParams();
  // With no `cal=` in the URL, the laptop calendar opens on the view this
  // browser last used (`calendar/calendar-prefs.ts`), Day the first time.
  const [rememberedCal, setRememberedCal] = useState(() => loadCalendarView() ?? 'day');
  const urlState = useMemo(
    () => parseCalendarUrl(searchParams, rememberedCal),
    [searchParams, rememberedCal],
  );
  const view = urlState.view;
  const navigateCalendar = useCallback(
    (patch: Partial<CalendarUrlState>, opts: { replace?: boolean } = {}) =>
      setSearchParams((prev) => writeCalendarUrl(prev, patch, rememberedCal), {
        replace: opts.replace ?? false,
      }),
    [setSearchParams, rememberedCal],
  );
  const setView = useCallback(
    (v: 'table' | 'calendar') => {
      // The drawer belongs to the tab it was opened on; leaving closes it
      // (it stays mounted, so a half-written task survives the trip).
      setFormOpen(false);
      navigateCalendar({ view: v });
    },
    [navigateCalendar],
  );
  /** Which element opened the new-task drawer, for focus to return to. */
  const drawerTriggerRef = useRef<HTMLElement | null>(null);
  // "+ New task" opens the dispatch form in a drawer over the table
  // (`NewTaskDrawer.tsx`); closed by default, on every screen. Closed, the
  // form stays MOUNTED and hidden, so a half-filled form survives it.
  const [formOpen, setFormOpen] = useState(false);
  const newTaskButtonRef = useRef<HTMLButtonElement>(null);
  const closeForm = useCallback(() => setFormOpen(false), []);
  /** The start the Month view's "New task on this day" filled in. Cleared
   *  by every other way in: the form then opens as it was left. */
  const [drawerPrefill, setDrawerPrefill] = useState<Date | undefined>(undefined);

  // EDITING A SAVED SCHEDULE (2026-10-30): the same drawer shell on the
  // same right-hand side (`ScheduleEditDrawer`). Like the new-task form,
  // closing only HIDES it: a half-made edit stays mounted -- a stray
  // press on the calendar costs nothing -- and Edit on the same schedule,
  // from anywhere, reopens it (moving its "Applies from" to the new run
  // unless the operator set it). A form holding NO change is never
  // reused: Edit opens a fresh one (a new `key`) on the schedule as it is
  // now, so a drag, a pause or another person's save since is what it
  // shows and what its PATCH is built on.
  const [editing, setEditing] = useState<{
    scheduleId: string;
    applyFromMs: number | null;
    key: number;
  } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const editTriggerRef = useRef<HTMLElement | null>(null);
  // The mounted form's own word on whether it holds a change.
  const editDirtyRef = useRef(false);
  const onEditDirty = useCallback((dirty: boolean) => {
    editDirtyRef.current = dirty;
  }, []);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const clearSavedNote = useCallback(() => setSavedNote(null), []);
  // The schedule just saved, for the lanes: a run's panel the editor
  // was opened from finds that run again under its new id.
  const [savedSchedule, setSavedSchedule] = useState<{
    scheduleId: string;
    nonce: number;
  } | null>(null);
  const openScheduleEditor = useCallback((req: EditScheduleRequest) => {
    editTriggerRef.current =
      req.trigger ??
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const preset = req.applyFromMs ?? null;
    const keep = editDirtyRef.current;
    setFormOpen(false);
    setEditing((cur) =>
      cur && cur.scheduleId === req.scheduleId && keep
        ? { ...cur, applyFromMs: preset }
        : { scheduleId: req.scheduleId, applyFromMs: preset, key: Date.now() },
    );
    setEditOpen(true);
  }, []);
  const reloadScheduleEditor = useCallback(
    () => setEditing((cur) => (cur ? { ...cur, key: Date.now() } : cur)),
    [],
  );
  const closeScheduleEditor = useCallback(() => setEditOpen(false), []);
  const onScheduleSaved = useCallback(
    (saved: TaskSchedule, keptUntilMs: number | null) => {
      setEditOpen(false);
      setEditing(null);
      setSavedSchedule({ scheduleId: saved.schedule_id, nonce: Date.now() });
      // A carry-over was made: the second schedule in the lists is the
      // old time, kept until the change day.
      setSavedNote(
        keptUntilMs !== null
          ? `Schedule updated. Runs ${endText(keptUntilMs, saved.timezone || siteZone.timeZone, Date.now())} keep their old time.`
          : 'Schedule updated',
      );
      // The control it was opened from (the panel's Edit…) is still there.
      // A timing edit gives a selected RUN a new id once the lanes
      // refetch; the lanes find it again (`savedSchedule`), so the
      // panel and this button stay.
      if (editTriggerRef.current?.isConnected) editTriggerRef.current.focus();
    },
    [siteZone.timeZone],
  );
  // THE ID, NOT THE RECORD. Holding the row itself made the drawer a
  // snapshot taken at click time: a queued task opened from the table
  // stayed queued on screen however long it ran, so the new Planned /
  // Visited pair could never be seen filling in. Deriving from `rows`
  // instead means the open drawer follows the live task -- and closes
  // itself if the task is tombstoned out of the list.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Slot-based creation: clicking an empty calendar cell, dragging an
  // existing event to a new time, or clicking the "New task" header
  // button opens the modal with start/end pre-filled.
  const [createSlot, setCreateSlot] = useState<CalendarSlot | null>(null);
  // `null` modal slot = "open the modal without a slot prefill" (the
  // header New Task button uses this so the form opens at "now").
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Scheduled occurrences are virtual — the server expands the site's
  // recurrence rules for whatever window the calendar is showing, so the
  // query follows the view's window (none while the table is up).
  // The lanes' day or week, on the SITE's calendar; today when the URL
  // names no day -- moved on at midnight, whether or not anything else
  // re-renders the screen then (`useTodayKey`).
  const todayKey = useTodayKey(siteZone.timeZone);
  const calDateKey: DateKey = urlState.date ?? todayKey;
  const lanesWindow = useMemo<OccurrenceWindow | null>(() => {
    // The month's days on the site's calendar, like the lanes' (the Month
    // view was rebuilt on them on 2026-10-31; it read the browser's clock).
    if (urlState.cal === 'month') return monthWindow(calDateKey, siteZone.timeZone);
    // The Agenda reads exactly the days it shows: the chosen day and the
    // ones after it (`days=`, two unless "Show next 3 days" grew it).
    if (urlState.cal === 'agenda') return agendaWindow(calDateKey, urlState.days, siteZone.timeZone);
    return urlState.cal === 'day'
      ? dayWindow(calDateKey, siteZone.timeZone)
      : weekWindow(calDateKey, siteZone.timeZone);
  }, [urlState.cal, urlState.days, calDateKey, siteZone.timeZone]);
  // ON A PHONE the calendar is Now / Today / Schedules (`PhoneCalendar`),
  // whatever the URL's laptop view says, and reads today and tomorrow:
  // today for its lists, tomorrow as well for each schedule's next run.
  const isPhone = useMediaQuery(PHONE_QUERY);
  // ON A PHONE ONE TAB BAR picks both the view and the calendar's tab:
  // Table · Now · Today · Schedules (`TasksPhoneTabs`). The URL keeps the
  // two as they always were -- `view=table|calendar` and the calendar's
  // `ptab` -- so links, Back and a reload behave exactly as before: a
  // move between the table and the calendar is a history entry (as the
  // old Table / Calendar switch was), a move between calendar tabs
  // replaces it (as the old Now / Today / Schedules switch did).
  const phoneTab: TasksPhoneTab = view === 'table' ? 'table' : parsePhoneUrl(searchParams).tab;
  const choosePhoneTab = useCallback(
    (tab: TasksPhoneTab) => {
      if (tab === 'table') {
        setView('table');
        return;
      }
      if (view === 'table') {
        setFormOpen(false);
        setSearchParams((prev) =>
          writePhoneUrl(writeCalendarUrl(prev, { view: 'calendar' }), { tab, sheet: null }),
        );
        return;
      }
      setSearchParams((prev) => writePhoneUrl(prev, { tab, sheet: null }), { replace: true });
    },
    [view, setView, setSearchParams],
  );
  // Remember the laptop calendar's view; a phone (Now / Today) never does.
  useEffect(() => {
    if (view !== 'calendar' || isPhone) return;
    saveCalendarView(urlState.cal);
    setRememberedCal(urlState.cal);
  }, [view, isPhone, urlState.cal]);
  const phoneTodayKey = todayKey;
  const phoneWindow = useMemo<OccurrenceWindow>(
    () => ({
      fromMs: dayWindow(phoneTodayKey, siteZone.timeZone).fromMs,
      toMs: dayWindow(addDays(phoneTodayKey, 1), siteZone.timeZone).toMs,
    }),
    [phoneTodayKey, siteZone.timeZone],
  );
  const calendarWindow = isPhone ? phoneWindow : lanesWindow;
  const schedules = useSchedules();
  const occurrences = useScheduleOccurrences(view === 'calendar' ? calendarWindow : null);
  // Same window the occurrences query uses, so the band and the
  // occurrences inside it are always fetched for the same range.
  const maintenanceWindows = useMaintenanceWindows(
    view === 'calendar' && calendarWindow
      ? { fromMs: calendarWindow.fromMs, toMs: calendarWindow.toMs }
      : null,
  );
  const createMaintenance = useCreateMaintenanceWindow();
  const deleteMaintenance = useDeleteMaintenanceWindow();
  const skipOccurrence = useSkipOccurrence();
  const overrideOccurrence = useOverrideOccurrence();
  const updateSchedule = useUpdateSchedule();
  const deleteSchedule = useDeleteSchedule();
  const setScheduleEnabled = useSetScheduleEnabled();
  /** Pause / Resume pressed on a run's panel or in the Month list: its
   *  own request, so what it came to is said there (`toggleFromPanel`)
   *  and never turns up in a scope dialog opened later. */
  const panelSetEnabled = useSetScheduleEnabled();
  const refreshSchedules = useRefreshSchedules();
  /** The rows of one plan, paused, resumed or deleted together: in
   *  flight, and -- when one of them failed -- which changed and which
   *  did not (`plan-batch`). */
  const [planBatchPending, setPlanBatchPending] = useState(false);
  const [planNote, setPlanNote] = useState<{
    title: string;
    text: string;
    /** The dialog it was chosen in, which says it too while open. */
    from: object | null;
  } | null>(null);

  /** A drag or a delete on a recurring occurrence, parked until the
   *  operator says whether they meant this one or the whole series. */
  const [pendingEdit, setPendingEdit] = useState<
    | { mode: 'move'; occurrence: ScheduleOccurrence; newStartMs: number }
    | { mode: 'delete'; occurrence: ScheduleOccurrence }
    | null
  >(null);
  // The dry run behind the move dialog's "This and following runs" line
  // (`followingRunsNote`): what it changes, and the runs before it the
  // server keeps at their old time.
  const pendingFollowing = pendingIsSingle() ? null : followingRunsPatch();
  const pendingFollowingId = pendingFollowing ? pendingEdit!.occurrence.schedule_id : null;
  const followingPreview = useScheduleUpdatePreview(pendingFollowingId, pendingFollowing);
  // Whether that dry run is asked at all -- not with no rule to shift
  // (the list did not load: the move falls back to this run alone), and
  // not in a "View as" preview -- so the dialog waits only for one that
  // is coming. An earlier body's answer, still on screen, is not this
  // one's.
  const previewRole = usePreviewRole();
  const followingExpected = pendingFollowingId !== null && previewRole === null;
  const followingFailed = followingExpected && followingPreview.isError;
  const followingChecking =
    followingExpected &&
    !followingFailed &&
    (followingPreview.data === undefined || followingPreview.isPlaceholderData);
  // Withholding the move/delete callbacks (rather than letting them
  // fail at the API) is what makes the calendar stop offering a drag
  // handle and a × at all — an affordance that always 403s is worse
  // than one that is absent, and the calendar says which it is.
  const scheduleEditsAllowed = roleAllowed && !writeDisabled;
  /** Edit is laptop-and-tablet only: the phone's calendar is for what
   *  is happening now (start, skip, pause), and the editor is a form.
   *  The front desk's iPad in portrait (768px and up) gets the laptop
   *  views, and Edit with them. Not held by fire mode: an edit changes a
   *  plan and dispatches nothing. */
  const onEditSchedule = scheduleEditsAllowed && !isPhone ? openScheduleEditor : undefined;
  /** Booking maintenance needs a strictly narrower role than
   *  dispatching does: `foh_operator` can schedule tasks and cannot
   *  stop the site. Withheld rather than disabled, so the control is
   *  absent for a role that could only ever be refused. */
  const maintenanceEditsAllowed =
    role !== null && MAINTENANCE_MANAGE_ROLES.includes(role) && !writeDisabled;

  const scheduleMutationError =
    skipOccurrence.error ??
    overrideOccurrence.error ??
    updateSchedule.error ??
    deleteSchedule.error ??
    setScheduleEnabled.error;

  /**
   * A failed schedule READ must be said out loud on the calendar.
   *
   * Both reads below feed the grid through `?? []`, so a 404 or a 500
   * renders a calendar that is pixel-identical to "nothing is
   * scheduled" — the one thing an operator uses this view to check.
   * The two failures are not the same failure and do not get the same
   * sentence: losing `occurrences` empties the grid, whereas losing
   * `schedules` leaves every occurrence drawn and only degrades what a
   * whole-series edit can do (see `applyScope`'s fallback).
   */
  const occurrencesFailed = occurrences.isError;
  const schedulesFailed = schedules.isError;
  const scheduleReadError = occurrences.error ?? schedules.error;
  const scheduleEditPending =
    skipOccurrence.isPending ||
    overrideOccurrence.isPending ||
    updateSchedule.isPending ||
    deleteSchedule.isPending ||
    planBatchPending;
  const occurrencePage = occurrences.data;
  const occurrenceRows = occurrencePage?.occurrences ?? [];
  // The server caps a window's expansion site-wide. A capped response
  // and a quiet fortnight draw the same calendar, so the cap has to be
  // said out loud — it used to reach only the api-server's own log,
  // which is on the wrong side of the network from the operator.
  const occurrencesTruncated = occurrencePage?.truncated === true;

  const rows = useMemo(
    () => scopeByActiveSite(tasks.data ?? [], site?.id ?? '').slice(),
    [tasks.data, site?.id],
  );

  // DISTANCE AND CLEANING TIME for the live rows. The live stream keeps
  // every task's totals on every console (`useLiveData`), so this tab
  // only has to catch up on totals sent before this console was
  // listening -- a read when the tab opens, for the rows it shows (and
  // for any the snapshot adds after it), and none on a console that
  // never opens it. See `useTaskDistanceCatchUp`.
  const distances = useTaskDistances(site?.id ?? '');
  useTaskDistanceCatchUp(site?.id ?? '', rows);

  // The open drawer's task, derived rather than stored. See `selectedId`.
  const selected = useMemo(
    () => (selectedId ? (rows.find((r) => r.id === selectedId) ?? null) : null),
    [rows, selectedId],
  );

  // AND CLOSING IS TERMINAL, which it was not.
  //
  // `selectedId` was only ever cleared by the drawer's own `onClose`,
  // which cannot fire once the drawer has unmounted -- so a task
  // leaving `rows` closed the drawer while leaving the id set, and the
  // drawer reopened by itself the moment the row came back. Two
  // ordinary ways that happens: switching site in the top bar empties
  // `rows` (this screen has no `key`, so its state survives the
  // switch), and switching back serves the cached list; and a
  // tombstone followed by a fresh state on the live wire, which
  // `category-reducers` documents as a supported sequence. Either way
  // the drawer springs open over whatever the operator moved on to,
  // with no gesture from them.
  useEffect(() => {
    if (selectedId && !selected) setSelectedId(null);
  }, [selectedId, selected]);

  // Table-view filters. The calendar view intentionally ignores
  // these — it has its own date-based navigation, and "filtering the
  // columns" doesn't map cleanly onto a calendar grid.
  const [statusFilter, setStatusFilter] = useState<'all' | TaskState['status']>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | string>('all');
  const [searchText, setSearchText] = useState('');

  // OLDER TASKS. Opening the view freezes where its first page starts
  // (`olderStartBeforeMs`) and which site it is for: a start that followed
  // the live list would re-key the read and drop every loaded page each
  // time a task finished, and a site switch must not show the last site's
  // archive. Nothing is read until this is set -- see `useOlderTasks`.
  const [olderStart, setOlderStart] = useState<{ siteId: string; beforeMs: number | null } | null>(
    null,
  );
  const olderOpen = olderStart !== null && olderStart.siteId === (site?.id ?? '');
  const older = useOlderTasks(site?.id ?? '', olderOpen ? olderStart.beforeMs : undefined);
  // Where the older view would start if it opened now. Once the live list
  // loses finished tasks above the frozen start, this moves past it, and
  // the tasks in between are read on their own -- see `useOlderTasksGap`.
  const liveStartBeforeMs = useMemo(() => olderStartBeforeMs(rows), [rows]);
  const olderGap = useOlderTasksGap(
    site?.id ?? '',
    olderOpen ? olderStart.beforeMs : undefined,
    liveStartBeforeMs,
  );
  const [selectedOlderId, setSelectedOlderId] = useState<string | null>(null);
  /** A finished task opened from the calendar lanes that the live list no
   *  longer holds: shown in the older-task drawer, from its record. */
  const [calendarOlder, setCalendarOlder] = useState<OlderTask | null>(null);

  // A task belongs on ONE list, and the live one is where its controls
  // are: anything still in the live feed is left out of the older one.
  const liveIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const olderRows = useMemo(
    () =>
      olderOpen
        ? olderRowsFrom(
            [{ entries: olderGap.entries, next_cursor: null }, ...(older.data?.pages ?? [])],
            liveIds,
          )
        : [],
    [olderOpen, olderGap.entries, older.data, liveIds],
  );
  const selectedOlder = useMemo(
    () =>
      olderOpen && selectedOlderId
        ? (olderRows.find((r) => r.id === selectedOlderId) ?? null)
        : null,
    [olderOpen, olderRows, selectedOlderId],
  );

  // Closing is terminal here too, for the reason `selectedId`'s effect
  // above gives: a row that left the list (a site switch, the view
  // closing) must not spring its panel open again when it comes back.
  useEffect(() => {
    if (selectedOlderId && !selectedOlder) setSelectedOlderId(null);
  }, [selectedOlderId, selectedOlder]);

  // ONE DRAWER AT A TIME. The new-task drawer and a task's detail drawer
  // take the same place on the right, so opening either closes the
  // other. A row click already does this on its own (its press is outside
  // the new-task drawer); this is for the openings that involve no press
  // there -- an alert's deep link, a keyboard Enter on a row. The form
  // is only hidden, so nothing written in it is lost.
  useEffect(() => {
    if (selectedId !== null || selectedOlderId !== null || calendarOlder !== null) setFormOpen(false);
  }, [selectedId, selectedOlderId, calendarOlder]);
  // ...and the schedule editor sits there too: whatever else takes the
  // right-hand side hides it (still mounted, still holding the edit).
  useEffect(() => {
    if (formOpen || selectedId !== null || selectedOlderId !== null || calendarOlder !== null) {
      setEditOpen(false);
    }
  }, [formOpen, selectedId, selectedOlderId, calendarOlder]);
  const toggleForm = () => {
    if (formOpen) {
      setFormOpen(false);
      return;
    }
    setSelectedId(null);
    setSelectedOlderId(null);
    setCalendarOlder(null);
    setDrawerPrefill(undefined);
    drawerTriggerRef.current = newTaskButtonRef.current;
    setFormOpen(true);
  };

  // "+ New task" pressed on another screen (Fleet): open the form on arrival,
  // once, and take the request out of the history entry so a reload or a
  // Back to here does not open it again.
  const location = useLocation();
  const navigate = useNavigate();
  // The lift panel's "Add to calendar…": Block time, on that lift. Taken into
  // state and off the history entry, so Back to here does not ask again.
  const [blockPreset, setBlockPreset] = useState<BlockTimePreset | null>(null);
  const blockRequested = (location.state as { openBlockTime?: BlockTimePreset } | null)?.openBlockTime;
  useEffect(() => {
    if (!blockRequested) return;
    setBlockPreset(blockRequested);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [blockRequested, location.pathname, location.search, navigate]);
  const openNewTaskRequested =
    (location.state as { openNewTask?: boolean } | null)?.openNewTask === true;
  useEffect(() => {
    if (!openNewTaskRequested) return;
    setSelectedId(null);
    setSelectedOlderId(null);
    setCalendarOlder(null);
    setDrawerPrefill(undefined);
    setFormOpen(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [openNewTaskRequested, location.pathname, location.search, navigate]);

  // A START FROZEN AT "NEWEST" IS TAKEN AGAIN once the live list has one.
  //
  // The view freezes its start when it opens, and with no finished task
  // live it starts from the newest finish on record. That is right for a
  // new site -- but an empty live list is also what a FAILED snapshot
  // looks like. When that list then loads, every row the older view had
  // is a duplicate of a live one and is dropped, and the button reads
  // "Nothing older yet" for the dozen-odd presses it takes to page past a
  // fortnight of tasks already on the recent list (`olderStartBeforeMs`
  // calls that "the button would look broken"). So the start moves to
  // where the view would start now -- but only while nothing it loaded
  // survives the de-duplication, which is exactly when moving loses
  // nothing. A new site's genuinely older rows stay where they are.
  useEffect(() => {
    if (!olderOpen || olderStart.beforeMs !== null || liveStartBeforeMs === null) return;
    if (olderRows.length > 0) return;
    setOlderStart({ siteId: olderStart.siteId, beforeMs: liveStartBeforeMs });
  }, [olderOpen, olderStart, liveStartBeforeMs, olderRows.length]);

  // FOCUS FOLLOWS THE SWITCH. Each of the two buttons that move between
  // the lists unmounts itself when pressed, which drops a keyboard user's
  // focus to the page body -- the next Tab then starts again at the side
  // nav. So the press says where focus goes once the other list is up:
  // the older list's heading, or back onto the button that opens it. A
  // ref, not state, and set only by these two presses: the older view
  // also appears and disappears on a site switch, and that must not pull
  // focus anywhere.
  const olderHeadingRef = useRef<HTMLHeadingElement>(null);
  const olderOpenButtonRef = useRef<HTMLButtonElement>(null);
  const focusAfterSwitch = useRef<'older' | 'recent' | null>(null);
  useEffect(() => {
    const target = focusAfterSwitch.current;
    if (target === null) return;
    focusAfterSwitch.current = null;
    (target === 'older' ? olderHeadingRef : olderOpenButtonRef).current?.focus();
  }, [olderOpen]);

  function openOlder() {
    setSelectedId(null);
    focusAfterSwitch.current = 'older';
    setOlderStart({ siteId: site?.id ?? '', beforeMs: liveStartBeforeMs });
  }

  function closeOlder() {
    setSelectedOlderId(null);
    focusAfterSwitch.current = 'recent';
    setOlderStart(null);
  }

  // The type filter offers every type on screen, from either list: an
  // older list can hold a kind of job the last fortnight has not run.
  //
  // AND THE ONE CHOSEN, WHETHER OR NOT A ROW STILL HAS IT. The filter
  // outlives the list it was picked from: a type found only in older
  // tasks stays applied after "Back to recent tasks", where no row has
  // it. A <select> whose value matches no option shows its first one, so
  // the screen read "All types" over "No tasks match the current
  // filters", with nothing to say which filter was doing it.
  //
  // A CLEAN IS FILED UNDER ITS MODE -- vacuum, sweep, dust-mop and scrub are four
  // types here, as in the Type cells. A live clean's mode comes from its
  // phases, else from the dispatch request a cell has already read; the
  // cache version re-files a queued clean once its request lands. One
  // whose mode is not known yet matches every mode rather than hiding.
  const typeQc = useQueryClient();
  const requestCacheVersion = useTaskRequestCacheVersion();
  const liveTypeKeys = useMemo(() => {
    void requestCacheVersion; // re-read the cache when a request lands
    const out = new Map<string, readonly string[]>();
    for (const t of rows) {
      const mode = liveCleanMode(
        t,
        t.type === 'clean' ? readCachedTaskRequest(typeQc, site?.id ?? '', t.id) : undefined,
      );
      out.set(t.id, mode === null ? CLEAN_MODES : [taskTypeKey(t.type, mode)]);
    }
    return out;
  }, [rows, typeQc, site?.id, requestCacheVersion]);
  const keysOfLive = (t: TaskState) => liveTypeKeys.get(t.id) ?? [t.type];
  const keysOfOlder = (t: { type: string; cleanMode?: Parameters<typeof taskTypeKey>[1] }) => [
    taskTypeKey(t.type, t.cleanMode),
  ];

  const availableTypes = useMemo(() => {
    const types = new Set<string>();
    for (const keys of liveTypeKeys.values()) if (keys.length === 1) types.add(keys[0]!);
    for (const r of olderRows) types.add(taskTypeKey(r.type, r.cleanMode));
    if (typeFilter !== 'all') types.add(typeFilter);
    return Array.from(types).sort();
  }, [liveTypeKeys, olderRows, typeFilter]);

  const filteredRows = useMemo(
    () =>
      rows.filter((t) =>
        matchesTaskFilters(t, statusFilter, typeFilter, searchText, liveTypeKeys.get(t.id) ?? [t.type]),
      ),
    [rows, statusFilter, typeFilter, searchText, liveTypeKeys],
  );

  // The filters apply to whichever list is on screen; for the older one
  // they narrow the pages loaded so far. Latest finish first, the order
  // the pages arrive in (`OLDER_FINISHED_COLUMN`), for the reason
  // `tableRows` hands the live table its order: it is what the rows do
  // if the table is left with no sort to apply.
  const filteredOlderRows = useMemo(
    () =>
      olderRows
        .filter((t) => matchesTaskFilters(t, statusFilter, typeFilter, searchText, keysOfOlder(t)))
        .sort((a, b) => b.completedAtMs - a.completedAtMs),
    [olderRows, statusFilter, typeFilter, searchText],
  );

  /**
   * NEWEST FIRST BEFORE THE TABLE SORTS ANYTHING. The table's default
   * sort is the Submitted column, and a person can now hide that
   * column; the table then has no column to sort by and keeps the order
   * it was handed. Handing it this order makes "Submitted, newest
   * first" what the rows still do with Submitted out of sight, instead
   * of the store's insertion order. Every other sort starts from here.
   */
  const tableRows = useMemo(
    () => [...filteredRows].sort(bySubmittedNewestFirst),
    [filteredRows],
  );

  // This person's column layout, per browser (`user-preferences.ts`).
  const { prefs, update: updatePrefs } = useUserPreferences();
  const savedLayout = prefs.tableLayouts[LAYOUT_KEY];
  const layout = useMemo(
    () => resolveTableLayout(TASK_COLUMN_SPECS, savedLayout),
    [savedLayout],
  );
  function saveLayout(next: ResolvedColumn[]) {
    const { [LAYOUT_KEY]: _previous, ...otherTables } = prefs.tableLayouts;
    updatePrefs({
      // A layout identical to the default is stored as NO layout, so a
      // later release's new default reaches this person too.
      tableLayouts: isDefaultLayout(TASK_COLUMN_SPECS, next)
        ? otherTables
        : { ...otherTables, [LAYOUT_KEY]: layoutToSave(next) },
    });
  }

  // LOCATION SORTS BY WHAT WAS KNOWN WHEN THE PERSON SORTED.
  //
  // A row whose phases name a place sorts by it, live. A row whose
  // phases name none shows the place its dispatch request planned, which
  // its cell reads only once the row is on screen -- and THAT is read
  // here once, when the person picks the Location sort, not each time a
  // read lands. Re-sorting on every landed read is a chain: the row that
  // learns its place moves away, the next unread row slides into view,
  // its cell reads, it moves away... until every row in a thousand has
  // been read, a screenful at a time. Held still, a place that arrives
  // after the sort shows in its cell and the row moves the next time the
  // person sorts.
  //
  // Rows not read when the sort is chosen go LAST, either way round
  // (`null`, see `DataTableColumn.sortValue`), so the top of the list --
  // the rows on screen -- is in order and reads nothing.
  const queryClient = useQueryClient();
  const [requestPlaces, setRequestPlaces] = useState<ReadonlyMap<string, string | null> | null>(
    null,
  );
  function onSortChosen(sort: DataTableSort | null) {
    if (sort?.columnId !== 'location') {
      setRequestPlaces(null);
      return;
    }
    const places = new Map<string, string | null>();
    for (const t of rows) {
      const record = readCachedTaskRequest(queryClient, site?.id ?? '', t.id);
      places.set(
        t.id,
        record === undefined
          ? null
          : (locationFromRequest(record?.request?.category ?? t.type, record?.request)?.text ?? ''),
      );
    }
    setRequestPlaces(places);
  }

  const columns = useMemo(() => {
    const defs = taskColumnDefs({
      locationSortValue: (t) => locationFromPhases(t)?.text ?? requestPlaces?.get(t.id) ?? null,
      distanceOf: (t) => distances.get(t.id),
    });
    return layout.filter((c) => c.visible).map((c) => defs[c.id as keyof typeof defs]);
  }, [layout, requestPlaces, distances]);

  // The SAME layout over the older rows: one person, one choice of
  // columns, whichever list they are reading -- behind the older list's
  // own Finished column, which it is paged and sorted by.
  const olderColumns = useMemo(() => {
    const defs = olderTaskColumnDefs();
    return [
      OLDER_FINISHED_COLUMN,
      ...layout.filter((c) => c.visible).map((c) => defs[c.id as TaskColumnId]),
    ];
  }, [layout]);

  const columnChoices = useMemo(
    () =>
      layout.map((c) => {
        const spec = TASK_COLUMN_SPECS.find((s) => s.id === c.id)!;
        return { id: c.id, label: spec.label, visible: c.visible, hideable: spec.hideable };
      }),
    [layout],
  );

  // Listen for deep-link clicks from the alert bell + toast (task
  // alerts). The matching drawer opens here; this mirrors the Fleet
  // screen's behaviour for robot alerts.
  useEffect(() => {
    function onDeepLink(e: Event) {
      const ce = e as CustomEvent<AlertDeepLinkKind>;
      const detail = ce.detail;
      if (!detail || detail.kind !== 'task') return;
      const match = taskForDeepLink(tasks.data ?? [], detail.taskId);
      if (match) {
        // A live task opens over the live list, so leave the older one.
        setSelectedOlderId(null);
        setOlderStart(null);
        setSelectedId(match.id);
        setView('table');
      }
    }
    window.addEventListener(ALERT_DEEP_LINK_EVENT, onDeepLink);
    return () => window.removeEventListener(ALERT_DEEP_LINK_EVENT, onDeepLink);
  }, [tasks.data, setView]);

  /** A maintenance refusal to show instead of opening the form, or
   *  null. Advisory only -- the server refuses on the command path
   *  regardless -- but opening a form whose submit is guaranteed to
   *  409 wastes the operator's time and reads as a bug. */
  const [slotRefusal, setSlotRefusal] = useState<string | null>(null);

  function handleSlot(slot: CalendarSlot) {
    const blocked = windowCovering(maintenanceWindows.data, slot.start.getTime());
    if (blocked) {
      setSlotRefusal(
        // Remove, never "shorten": the console cannot shorten a blocked
        // time (2026-10-31).
        `That time falls in blocked time${
          blocked.reason ? ` (${blocked.reason})` : ''
        }, so nothing can be dispatched then. Remove the blocked time to book work in it.`,
      );
      return;
    }
    setSlotRefusal(null);
    setCreateSlot(slot);
    setCreateModalOpen(true);
  }

  /** Pause or resume schedules -- a schedule and the other rows of its
   *  plan -- with `done` once every one has gone through. One is the
   *  plain `mutate` it always was. */
  function setEnabledAll(
    ids: readonly string[],
    enabled: boolean,
    done?: { onSuccess: () => void },
  ) {
    if (ids.length <= 1) {
      if (ids[0] !== undefined) setScheduleEnabled.mutate({ scheduleId: ids[0], enabled }, done);
      return;
    }
    void runPlanBatch(
      enabled ? 'resume' : 'pause',
      ids,
      (id) => setScheduleEnabled.mutateAsync({ scheduleId: id, enabled }),
      done,
    );
  }

  function deleteAll(ids: readonly string[], done?: { onSuccess: () => void }) {
    if (ids.length <= 1) {
      if (ids[0] !== undefined) deleteSchedule.mutate(ids[0], done);
      return;
    }
    void runPlanBatch('delete', ids, (id) => deleteSchedule.mutateAsync(id), done);
  }

  /**
   * The rows of one plan, one request each, every one tried
   * (`eachInTurn`). All through: `done`. One failed: the schedules are
   * read again, so the calendar draws each row as it now is, and the
   * screen says which rows changed and which did not (`planNote`) -- the
   * dialog it came from closes when any did, since what it listed is no
   * longer so, and stays, with why, when none did.
   */
  function runPlanBatch(
    verb: PlanVerb,
    ids: readonly string[],
    act: (scheduleId: string) => Promise<unknown>,
    done?: { onSuccess: () => void },
  ): Promise<BatchOutcome> {
    const before = schedules.data ?? [];
    const now = Date.now();
    const from = done ? pendingEdit : null;
    setPlanNote(null);
    setPlanBatchPending(true);
    return eachInTurn(ids, act).then((outcome) => {
      setPlanBatchPending(false);
      const text = batchFailureText(verb, outcome, before, now, siteZone.timeZone);
      if (text === null) {
        done?.onSuccess();
        return outcome;
      }
      void refreshSchedules();
      setPlanNote({
        title: outcome.done.length > 0 ? 'Not every schedule changed' : 'Nothing changed',
        text,
        from,
      });
      if (outcome.done.length > 0 && done) {
        setPendingEdit(null);
        resetScheduleMutations();
      }
      return outcome;
    });
  }

  /**
   * Pause or resume from a run's panel or the Month list -- one
   * schedule, or (`ids` of more than one) the rows of one plan -- with
   * what it came to, in words, for the line under the button. One
   * schedule refused says why there: it was said nowhere before, outside
   * the scope dialog. The rows of a plan with one refused are said, row
   * by row, in the note above the calendar (`runPlanBatch`); the line
   * points to it.
   */
  function toggleFromPanel(ids: readonly string[], enabled: boolean): Promise<ToggleOutcome> {
    if (ids.length > 1) {
      return runPlanBatch(enabled ? 'resume' : 'pause', ids, (id) =>
        panelSetEnabled.mutateAsync({ scheduleId: id, enabled }),
      ).then((outcome) => {
        const past = enabled ? 'resumed' : 'paused';
        if (outcome.failed.length === 0) {
          return { message: toggledWords(enabled, true), tone: 'ok' as const };
        }
        return {
          message:
            outcome.done.length > 0
              ? `Not every schedule was ${past}. The note above the calendar says which.`
              : `Nothing was ${past}. The note above the calendar says why.`,
          tone: 'error' as const,
        };
      });
    }
    return panelSetEnabled.mutateAsync({ scheduleId: ids[0]!, enabled }).then(
      () => ({ message: toggledWords(enabled, false), tone: 'ok' as const }),
      (err: unknown) => toggleFailureWords(enabled, err),
    );
  }

  /** The other rows of the parked delete's schedule's plan: the old
   *  time kept before it (`keptOldTime`) and the new time after it
   *  (`newTimeAfter`, when this is the kept one). Still to run, so the
   *  dialog says they are there. */
  function pendingKept() {
    const schedule = pendingSchedule();
    if (!schedule || pendingEdit?.mode !== 'delete') return { earlier: [], later: [] };
    const all = schedules.data ?? [];
    return {
      earlier: keptOldTime(schedule, all, Date.now()),
      later: newTimeAfter(schedule, all, Date.now()),
    };
  }

  /** Resolve a parked occurrence edit once the operator has chosen the
   *  scope. "This event" addresses the occurrence by its ORIGINAL start
   *  (what the rule produced), never by where it currently sits, so a
   *  second edit still lands on the same occurrence. */
  function applyScope(scope: OccurrenceScope, wholePlan = false) {
    if (!pendingEdit) return;
    const { occurrence } = pendingEdit;
    const ref = {
      scheduleId: occurrence.schedule_id,
      originalStartMs: occurrence.original_start_ms,
    };
    const done = { onSuccess: () => setPendingEdit(null) };

    if (pendingEdit.mode === 'delete') {
      // "Pause all / Delete all 3": the other rows of its plan go with
      // this one only when the operator said so.
      const { earlier, later } = pendingKept();
      // Pause takes along only the rows still running: one already
      // paused has nothing to stop (`planChoice`'s `pauseCount`).
      const others = [...earlier, ...later].filter((k) => scope !== 'pause' || k.enabled);
      const ids = [occurrence.schedule_id, ...(wholePlan ? others.map((k) => k.schedule_id) : [])];
      if (scope === 'one') skipOccurrence.mutate(ref, done);
      // Offered beside "delete the series" because this is the moment
      // the operator is about to destroy a rule they only wanted to
      // stop. Pausing keeps the rule, its history and its fire ledger.
      else if (scope === 'pause') setEnabledAll(ids, false, done);
      else deleteAll(ids, done);
      return;
    }

    if (scope === 'one') {
      overrideOccurrence.mutate(
        { ...ref, override: { replacement_start_ms: pendingEdit.newStartMs } },
        done,
      );
      return;
    }

    const following = followingRunsPatch();
    if (!following) {
      // The series is not in the cache, so there is no rule to shift.
      // Fall back to moving just this occurrence rather than writing a
      // rule built from a guess.
      overrideOccurrence.mutate(
        { ...ref, override: { replacement_start_ms: pendingEdit.newStartMs } },
        done,
      );
      return;
    }
    updateSchedule.mutate({ scheduleId: occurrence.schedule_id, patch: following }, done);
  }

  /**
   * "This and following runs" as the PATCH it sends (2026-10-30): a
   * TIMING edit from this run on, which the api-server re-anchors in
   * place (0.6.54) -- runs before it and runs already done keep their
   * time and their outcomes, the schedule keeps its id. It replaced "All
   * events in this series", which rewrote the rule for the whole series
   * PAST INCLUDED: past outcomes fell off the calendar, skipped dates
   * came back and moved runs snapped home.
   *
   * The new rule is seeded at the run's NEW time -- where the operator
   * put it, an override's or not -- and its day selectors move with it,
   * decided on the SCHEDULE's calendar (`shiftFollowingRuns`). Null when
   * there is no rule to shift (the list did not load).
   *
   * A one-off booking ("Move it") has nothing to follow: its one start
   * moves, as it always did. It has not run, so the server applies the
   * new start as given.
   */
  function followingRunsPatch(): UpdateTaskScheduleRequest | null {
    if (!pendingEdit || pendingEdit.mode !== 'move') return null;
    const schedule = pendingSchedule();
    if (!schedule) return null;
    if (isOneShotRule(schedule.rrule)) return { dtstart_ms: pendingEdit.newStartMs };
    return shiftFollowingRuns(
      schedule.rrule,
      pendingEdit.occurrence.original_start_ms,
      pendingEdit.newStartMs,
      schedule.timezone,
    );
  }

  function closeScopeDialog() {
    setPendingEdit(null);
    resetScheduleMutations();
  }

  function resetScheduleMutations() {
    skipOccurrence.reset();
    overrideOccurrence.reset();
    updateSchedule.reset();
    deleteSchedule.reset();
    setScheduleEnabled.reset();
  }

  /** What the scope dialog says the choice will do.
   *
   *  Every clock in it is stamped with its zone. This sentence is the
   *  last thing between a drag and a rule rewrite that moves every
   *  future occurrence, and "Moving to 14:00" means nothing to a reader
   *  who cannot tell whose 14:00 it is. */
  /** The schedule behind a parked edit, when the list loaded. */
  function pendingSchedule() {
    if (!pendingEdit) return undefined;
    return (schedules.data ?? []).find(
      (s) => s.schedule_id === pendingEdit.occurrence.schedule_id,
    );
  }

  /**
   * A one-off booking, not a series — the shape a "schedule for later"
   * task now takes. There is nothing to choose a scope between, so the
   * dialog collapses to a confirm.
   *
   * Unknown when the schedule list failed to load, and the answer then
   * is `false`: offering the full three-way choice on a series that
   * might be one occurrence is harmless, whereas hiding "all events"
   * from a real weekly clean would silently narrow what the operator
   * can do to it.
   */
  function pendingIsSingle(): boolean {
    const schedule = pendingSchedule();
    return schedule ? isOneShotRule(schedule.rrule) : false;
  }

  function pendingEditDetail(): string | undefined {
    if (!pendingEdit) return undefined;
    const schedule = pendingSchedule();
    // The occurrence carries its own schedule's zone, so the sentence
    // stays honest even when the schedule LIST failed to load and the
    // rule itself cannot be described.
    const zone = schedule?.timezone ?? pendingEdit.occurrence.timezone ?? siteZone.timeZone;
    const rule = schedule
      ? describeRule(schedule.rrule, schedule.dtstart_ms, schedule.until_ms, zone)
      : null;
    if (pendingEdit.mode === 'delete') {
      if (pendingIsSingle()) {
        return rule
          ? `${rule}. It has not been dispatched yet, so deleting it removes it entirely.`
          : undefined;
      }
      // "Skipping affects only this date" is the dialog's to say, beside
      // the choices that include Skip (`SKIP_ONLY_NOTE`).
      return rule ? `${rule}.` : undefined;
    }
    const to = `${formatZonedDateTime(pendingEdit.newStartMs, zone)} ${timeZoneLabel(zone, pendingEdit.newStartMs)}`;
    return rule ? `Moving to ${to}. ${rule}.` : `Moving to ${to}.`;
  }

  /** Where "This and following runs" stops: the row's own end ("until
   *  Tue 03/Nov"), which its PATCH keeps -- the old time kept on a
   *  carry-over, or an end someone set. Undefined for a row with none. */
  function followingRunsStop(): string | undefined {
    const schedule = pendingSchedule();
    if (!schedule || schedule.until_ms === null || schedule.until_ms === undefined) return undefined;
    return endText(schedule.until_ms, schedule.timezone || siteZone.timeZone, Date.now());
  }

  /** What "This and following runs" would do, from the server's dry run
   *  of the same PATCH: when the new time takes over, what it drops, and
   *  -- for a run further out than the next one -- the runs before it,
   *  which keep their old time (the server's carry-over). On a row with
   *  an end it moves the runs up to that end only, and a later row of
   *  its plan keeps its own time (a timing change cuts only EARLIER
   *  rows), so it says where it stops and who takes over. Nothing until
   *  the dry run answers. */
  function followingRunsNote(): string | undefined {
    const patch = followingRunsPatch();
    const schedule = pendingSchedule();
    const answer = followingPreview.data;
    if (!patch || !schedule || !answer || followingPreview.isPlaceholderData) return undefined;
    const tz = schedule.timezone || siteZone.timeZone;
    const now = Date.now();
    const parts: string[] = [];
    const end = schedule.until_ms ?? null;
    if (answer.first_run_ms !== null) {
      const until = end !== null ? endText(end, tz, now) : 'on';
      parts.push(
        `This and following runs: every run from ${runText(answer.first_run_ms, now, tz)} ${until}.`,
      );
    }
    const later = newTimeAfter(schedule, schedules.data ?? [], now);
    if (later.length > 0) {
      parts.push(
        `${newTimeAfterText(schedule.name, later, now, siteZone.timeZone)} ${
          later.length === 1
            ? 'It keeps its own time: to change it, move one of its runs too.'
            : 'They keep their own times: to change them, move one of their runs too.'
        }`,
      );
    }
    // The pattern from then on, when it is more than the old one at a new
    // time: a run already moved to another day takes the weekdays with it
    // (the shift is measured from the run's own rule instant), and the
    // dialog's other line describes the OLD rule.
    if (patch.rrule !== undefined && patch.dtstart_ms !== undefined) {
      const opts = { siteTimeZone: siteZone.timeZone };
      const was = describeRule(schedule.rrule, schedule.dtstart_ms, null, tz, opts);
      const next = describeRule(patch.rrule, patch.dtstart_ms, null, tz, opts);
      const clockOnly = was.replace(
        formatZonedClock(schedule.dtstart_ms, tz),
        formatZonedClock(patch.dtstart_ms, tz),
      );
      if (clockOnly !== next) {
        // Up to the row's end, which the PATCH keeps.
        const then = end !== null ? describeRule(patch.rrule, patch.dtstart_ms, end, tz, opts) : next;
        parts.push(`It will then run ${then[0]!.toLowerCase()}${then.slice(1)}.`);
      }
    }
    // By day: a skip it removes is a day the robot runs again.
    const dropped = droppedText(answer, now, tz);
    if (dropped) parts.push(dropped);
    // The runs before it keep their time: the server keeps them on a
    // second schedule of the same name until the change day.
    const kept = answer.timing_change
      ? keptText(answer.kept_runs_ms ?? [], answer.kept_until_ms ?? null, now, tz)
      : null;
    if (kept) parts.push(kept);
    // And the other rows of its plan it cuts short from that day.
    const clipped =
      answer.timing_change && patch.dtstart_ms !== undefined
        ? clippedText(
            answer.clipped ?? [],
            schedules.data ?? [],
            patch.dtstart_ms,
            now,
            tz,
            siteZone.timeZone,
            schedule.schedule_id,
          )
        : null;
    if (clipped) parts.push(clipped);
    return parts.length ? parts.join(' ') : undefined;
  }
  /** The delete dialog's word on the other rows of its plan: every row
   *  still to run and what it has left, this one first -- a paused one
   *  marked so, since it runs on none of those days -- so "Delete all
   *  3" and "Only this one" each say what they take
   *  (`OccurrenceScopeDialog`'s `plan`). Pause asks only about the rows
   *  still running (`pauseCount`), as the panels' Pause does
   *  (`planPartners`). */
  function planChoice() {
    const { earlier, later } = pendingKept();
    const schedule = pendingSchedule();
    const count = earlier.length + later.length;
    if (!schedule || count === 0) return undefined;
    const now = Date.now();
    const tz = siteZone.timeZone;
    const others = [...earlier, ...later].sort((a, b) => a.dtstart_ms - b.dtstart_ms);
    const times = count + 1 === 2 ? 'twice' : `${count + 1} times`;
    const label = (who: string, o: TaskSchedule) =>
      `${who}${o.enabled ? '' : ' (paused)'}: ${stillToRunText(o, now, tz)}`;
    return {
      intro: `"${schedule.name}" is listed ${times}, after a change of time:`,
      rows: [
        label('This one', schedule),
        ...others.map((o) => label(count === 1 ? 'The other' : 'Another', o)),
      ],
      count,
      pauseCount: others.filter((o) => o.enabled).length,
    };
  }

  function exportCsv() {
    // A FIXED SCHEMA, deliberately independent of the table. Each person
    // can now hide and reorder the on-screen columns, and a spreadsheet
    // or script built on this file reads its fields by position -- so
    // the export never follows anyone's layout: every field, always in
    // this order, whatever the table shows. (It used to mirror the
    // table's column order, back when there was only one.) This header
    // and the row builder below are two independent lists joined by
    // index and nothing else — they silently mis-label every column
    // after the first divergence, so any edit to one is an edit to
    // both. See `SUBMITTED_TOOLTIP` in `task-columns.tsx`:
    // `submitted_at` is the platform's receive time, not an operator
    // keystroke, which matters more in an exported file than on screen
    // because a spreadsheet carries no tooltip. Exported as ISO-8601
    // rather than the display format, matching `started_at`.
    //
    // NEW FIELDS GO ON THE END, never between: the distance fields
    // (2026-10-21, `TASK_CSV_DISTANCE_FIELDS`) follow `cleaning_area` so
    // every sheet already reading columns 1-13 by position reads them
    // unchanged. Blank, not 0, for a task nothing measured.
    const header = [
      'submitted_at,type,status,started_at,duration_sec,robot,task_id,triggered_by,destination,pickup,dropoff,waypoints,cleaning_area',
      ...TASK_CSV_DISTANCE_FIELDS,
    ].join(',');
    // Rows leave in the table's DEFAULT order (newest submission
    // first, see `TASKS_DEFAULT_SORT`), not in whatever order the
    // operator may have clicked into -- the table owns that state and
    // the export does not read it. The default is what an operator
    // sees on opening the screen, which is the case the export exists
    // for.
    const csvRows = [...filteredRows].sort(bySubmittedNewestFirst).map((t) => {
      const { text: triggeredBy } = triggeredByLabel(t.triggeredBy);
      const wp = waypointColumns(t);
      return [
        t.submittedAtMs ? new Date(t.submittedAtMs).toISOString() : '',
        // The type as on screen (`dust-mop`, `routine-check`): a clean as
        // its mode, one whose mode is not known yet as the vacuum it
        // defaults to.
        typeKeyText(
          keysOfLive(t).length === 1 ? keysOfLive(t)[0]! : taskTypeKey(t.type, undefined),
        ),
        t.status,
        t.startedAt ? new Date(t.startedAt).toISOString() : '',
        t.durationSec ?? '',
        t.assignedRobot ?? '',
        t.id,
        triggeredBy,
        wp.destination,
        wp.pickup,
        wp.dropoff,
        wp.waypoints,
        wp.cleaning_area,
        ...distanceCsvValues(distances.get(t.id)),
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',');
    });
    const blob = new Blob([[header, ...csvRows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tasks-${site?.id ?? 'site'}-${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** "Open task" from the lanes: the live drawer when the live list has
   *  it (with its controls), else the older-task drawer from the record. */
  const openLaneTask = useCallback((item: LaneItem) => {
    setFormOpen(false);
    if (item.live) {
      setCalendarOlder(null);
      setSelectedId(item.live.id);
    } else if (item.completion) {
      setSelectedId(null);
      setCalendarOlder(adaptCompletion(item.completion));
    }
  }, []);
  const dispatchAllowed = roleAllowed && !writeDisabled;
  const openNewTaskFromCalendar = useCallback(() => {
    setSelectedId(null);
    setSelectedOlderId(null);
    setCalendarOlder(null);
    setDrawerPrefill(undefined);
    drawerTriggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setFormOpen(true);
  }, []);
  /** The Month view's "New task on this day": the same drawer, its start
   *  filled in with that day (2026-10-31). */
  const openNewTaskOnDay = useCallback((start: Date) => {
    setSelectedId(null);
    setSelectedOlderId(null);
    setCalendarOlder(null);
    setDrawerPrefill(start);
    drawerTriggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setFormOpen(true);
  }, []);
  const renderTaskActions = useCallback((t: TaskState) => <TaskControlActions task={t} />, []);

  if (!site) {
    return (
      <div className="nx-tasks">
        <h1 className="nx-section-title">Tasks</h1>
        <NoSiteNotice />
      </div>
    );
  }

  const phoneCalendarShown = isPhone && view === 'calendar' && !tasks.isLoading;
  const phoneTabs = isPhone ? <TasksPhoneTabs value={phoneTab} onChange={choosePhoneTab} /> : null;
  /* Dispatch-unavailable banner is rendered OUTSIDE the role
     RoleGuard so operators with the wrong role can still see
     *why* they can't dispatch. Without this, service_operator
     visitors would see no explanation for the missing button. The
     roles by their names, not their keys: it read "Your role
     (nexsoss:service_operator) ... Sign in as a platform_admin, ..."
     above the Month (review, 2026-10-31). The other two causes in hotel
     words, with the flag and the readiness reading kept for ops (P9,
     2026-10-31): the front desk read "The `taskDispatchEnabled` feature
     flag is off in the runtime config". */
  const dispatchBanner = writeDisabled ? (
    <AlertBanner variant="info" title="Dispatch unavailable">
      {!roleAllowed
        ? `${role ? `Your role, ${previewRoleLabel(role)}, cannot` : 'You are not signed in with a role that can'} create tasks. ${DISPATCH_ROLES_TEXT} can.`
        : !dispatchFlagEnabled
          ? 'Creating tasks is switched off at this site. Call Nexsoss.'
          : 'New tasks are paused until the system is back.'}
      {audience === 'ops' && roleAllowed
        ? !dispatchFlagEnabled
          ? ' The `taskDispatchEnabled` feature flag is off in the runtime config.'
          : ` API readiness is "${readiness}"${readinessDetail ? ` (${readinessDetail})` : ''}.`
        : null}
    </AlertBanner>
  ) : null;
  const newTaskButton = (
    <RoleGuard allowedRoles={[...TASK_DISPATCH_ROLES]}>
      {!writeDisabled && view === 'table' ? (
        <button
          ref={newTaskButtonRef}
          type="button"
          className={`nx-tasks__new${formOpen ? ' nx-tasks__new--open' : ''}`}
          onClick={toggleForm}
          aria-expanded={formOpen}
          aria-controls="nx-tasks-dispatch"
          data-testid="tasks-new-toggle"
        >
          + New task
        </button>
      ) : null}
    </RoleGuard>
  );
  // Table-tab search and filters: under the tab bar on a phone, under
  // the header on a laptop.
  const filters =
    view === 'table' && (rows.length > 0 || olderOpen) ? (
      <div className="nx-tasks__filters">
        <input
          type="text"
          className="nx-tasks__search"
          placeholder="Search task ID, robot, triggered by…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          data-testid="tasks-search"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | TaskState['status'])}
          data-testid="tasks-filter-status"
        >
          <option value="all">All statuses</option>
          <option value="queued">Queued</option>
          <option value="active">Active</option>
          <option value="interrupted">Interrupted</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="failed">Failed</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="tasks-filter-type"
        >
          <option value="all">All types</option>
          {availableTypes.map((t) => (
            <option key={t} value={t}>
              {/* Vacuum, sweep, dust-mop and scrub are four types here, as
                  in the Type cells. */}
              {typeKeyText(t)}
            </option>
          ))}
        </select>
        {(statusFilter !== 'all' || typeFilter !== 'all' || searchText.trim()) && (
          <button
            type="button"
            className="nx-tasks__filters-clear"
            onClick={() => {
              setStatusFilter('all');
              setTypeFilter('all');
              setSearchText('');
            }}
          >
            Clear filters
          </button>
        )}
        <TaskColumnsControl
          columns={columnChoices}
          customised={savedLayout !== undefined}
          onToggle={(id, visible) =>
            saveLayout(setColumnVisible(TASK_COLUMN_SPECS, layout, id, visible))
          }
          onMove={(id, delta) => saveLayout(moveColumn(layout, id, delta))}
          onReset={() => saveLayout(resolveTableLayout(TASK_COLUMN_SPECS, null))}
        />
      </div>
    ) : null;

  // The calendar's banners: above the grid on a laptop; on a phone under
  // the calendar's sticky head (tab bar and chips), above the tab.
  const calendarBanners = (
    <>
      {(occurrencesFailed || schedulesFailed) && (
        // Above the grid, not below it: this banner exists to be
        // read BEFORE the empty calendar it is explaining.
        <div data-testid="tasks-schedule-error">
          <AlertBanner
            variant="error"
            title={
              occurrencesFailed
                ? 'Scheduled tasks could not be loaded'
                : 'Schedule details could not be loaded'
            }
            actions={
              <button
                type="button"
                className="nx-tasks__cta"
                data-testid="tasks-schedule-error-retry"
                onClick={() => {
                  if (occurrencesFailed) void occurrences.refetch();
                  if (schedulesFailed) void schedules.refetch();
                }}
              >
                Retry
              </button>
            }
          >
            {occurrencesFailed
              ? `The request for this site's scheduled tasks failed${
                  scheduleReadError ? ` (${scheduleReadError.message})` : ''
                }. Recurring work is MISSING from the calendar below — an empty grid here does not mean nothing is scheduled.`
              : `Scheduled tasks are shown, but this site's schedule list failed to load${
                  scheduleReadError ? ` (${scheduleReadError.message})` : ''
                }. Moving a run and choosing "This and following runs" will move only that run until it loads.`}
          </AlertBanner>
        </div>
      )}
      {occurrencesTruncated && (
        <AlertBanner variant="warning" title="Not every scheduled task is shown">
          <span data-testid="tasks-occurrences-truncated">
            This site has more scheduled occurrences in the visible range than one
            response carries ({occurrencePage?.limit ?? 0}). The calendar below is
            INCOMPLETE — narrow the range (Week or Day) to see the rest, or pause
            schedules that no longer need to run.
          </span>
        </AlertBanner>
      )}
      {planNote && (
        <AlertBanner
          variant="error"
          title={planNote.title}
          actions={
            <button
              type="button"
              className="nx-tasks__cta"
              onClick={() => setPlanNote(null)}
              data-testid="tasks-plan-note-dismiss"
            >
              Dismiss
            </button>
          }
        >
          <span data-testid="tasks-plan-note">{planNote.text}</span>
        </AlertBanner>
      )}
      {slotRefusal && (
        <AlertBanner variant="info" title="That period is closed">
          <span data-testid="tasks-maintenance-refusal">{slotRefusal}</span>
        </AlertBanner>
      )}
      {(createMaintenance.isError || deleteMaintenance.isError) && (
        <AlertBanner variant="error" title="Blocked time not saved">
          <span data-testid="tasks-maintenance-error">
            {(createMaintenance.error ?? deleteMaintenance.error)?.message ??
              'The change was not applied.'}
          </span>
        </AlertBanner>
      )}
      {zoneWarning && (
        <AlertBanner variant="warning" title="No site timezone">
          <span data-testid="tasks-zone-warning">{zoneWarning}</span>
        </AlertBanner>
      )}
    </>
  );

  return (
    <div
      className={`nx-tasks${phoneCalendarShown ? ' nx-tasks--phone-cal' : ' nx-page-fill'}`}
    >
      {isPhone ? (
        <header
          className="nx-tasks__head nx-tasks__head--phone"
          // The title row gives way to the content when the shell's bar
          // tucks (`use-scroll-tuck.ts`), on the table only: the calendar
          // tabs scroll it away with the page under their sticky head, and
          // collapsing it there would move the page under the reader's
          // thumb.
          data-tuck={view === 'table' ? 'hide' : undefined}
        >
          <div className="nx-tasks__title">
            <h1 className="nx-section-title">Tasks</h1>
            <TasksPhoneClock timeZone={siteZone.timeZone} />
          </div>
          <HelpScreenNote screen="tasks" />
          <div className="nx-tasks__head-actions">
            {newTaskButton}
          </div>
        </header>
      ) : (
        <header
          className="nx-tasks__head"
          // The title row folds away while the shell's bar is tucked
          // (`use-scroll-tuck.ts`), on the table and the calendar alike.
          // The calendar's lanes scroll inside their own box, so the
          // reader's place does not move when the row goes. (A single
          // header row with the calendar toolbar was the plan; at 1280 the
          // toolbar has no room left, so folding on scroll stands in.)
          data-tuck="hide"
        >
          <h1 className="nx-section-title">Tasks</h1>
          <HelpScreenNote screen="tasks" />
          <div className="nx-tasks__head-actions">
            {newTaskButton}
            <button
              type="button"
              className="nx-tasks__cta"
              onClick={exportCsv}
              // Recent tasks only. The older list is a few pages of a long
              // record, and a file of "whatever happened to be loaded" would
              // read as complete when it is not.
              disabled={filteredRows.length === 0 || (view === 'table' && olderOpen)}
              title={
                view === 'table' && olderOpen
                  ? 'Export covers recent tasks. Go back to recent tasks to export them.'
                  : 'Export the currently visible task list as CSV'
              }
              data-testid="tasks-export-csv"
            >
              Export CSV
            </button>
            <div className="nx-tasks__view-toggle" role="tablist" aria-label="View">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'table'}
                className={`nx-tasks__view-btn${view === 'table' ? ' nx-tasks__view-btn--active' : ''}`}
                onClick={() => setView('table')}
                data-testid="tasks-view-table"
              >
                Table
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'calendar'}
                className={`nx-tasks__view-btn${view === 'calendar' ? ' nx-tasks__view-btn--active' : ''}`}
                onClick={() => setView('calendar')}
                data-testid="tasks-view-calendar"
              >
                Calendar
              </button>
            </div>
          </div>
        </header>
      )}

      {/* PHONE: the tab bar, with the table's search and filters under it
          on the Table tab. On a calendar tab the calendar draws the bar
          itself, in its sticky head, with the group chips. */}
      {isPhone && !phoneCalendarShown && (
        <div className="nx-tasks__phone-bar" data-testid="tasks-phone-bar">
          {phoneTabs}
          {filters}
        </div>
      )}

      {!phoneCalendarShown && dispatchBanner}

      {!isPhone && filters}

      {tasks.isLoading ? (
        <SkeletonLoader shape="table-row" count={3} />
      ) : view === 'table' ? (
        // Wrapper carries the fill/scroll so the toolbar and filters
        // above stay pinned while only the rows move.
        <div
          className="nx-page-fill__body nx-tasks__body"
          {...(isPhone
            ? {
                role: 'tabpanel',
                id: tasksPhonePanelId('table'),
                'aria-labelledby': tasksPhoneTabId('table'),
              }
            : {})}
        >
          {olderOpen ? (
            <OlderTasksSection
              rows={filteredOlderRows}
              loadedCount={olderRows.length}
              columns={olderColumns}
              defaultSort={OLDER_TASKS_DEFAULT_SORT}
              isLoading={older.isLoading}
              isError={older.isError && !older.data}
              isFetchNextPageError={older.isFetchNextPageError}
              hasNextPage={older.hasNextPage}
              isFetchingNextPage={older.isFetchingNextPage}
              gapError={olderGap.isError}
              gapPartial={!olderGap.complete}
              onRetryGap={() => void olderGap.refetch()}
              onLoadMore={() => void older.fetchNextPage()}
              onRetry={() => void older.refetch()}
              onOpen={(t) => setSelectedOlderId(t.id)}
              onBack={closeOlder}
              headingRef={olderHeadingRef}
            />
          ) : (
            <>
              {rows.length === 0 ? (
                <AlertBanner variant="info" title="No tasks yet">
                  Start one with New task, above, and it will appear here.
                </AlertBanner>
              ) : filteredRows.length === 0 ? (
                <AlertBanner variant="info" title="No tasks match the current filters">
                  Try clearing the search or filter selections above.
                </AlertBanner>
              ) : (
                <DataTable
                  columns={columns}
                  rows={tableRows}
                  getRowId={(t) => t.id}
                  ariaLabel="Tasks"
                  onRowClick={(t) => setSelectedId(t.id)}
                  defaultSort={TASKS_DEFAULT_SORT}
                  onSortChosen={onSortChosen}
                />
              )}
              {/* Always offered, even on an empty list: an empty recent
                  list is exactly when the record behind it matters. */}
              <div className="nx-tasks__older-open-row">
                <button
                  ref={olderOpenButtonRef}
                  type="button"
                  className="nx-tasks__older-open"
                  onClick={openOlder}
                  data-testid="tasks-older-open"
                >
                  Older tasks
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          {!isPhone && calendarBanners}
          {isPhone ? (
            <PhoneCalendarContainer
              siteId={site.id}
              timeZone={siteZone.timeZone}
              timeZoneSource={siteZone.source}
              tasks={rows}
              occurrences={occurrenceRows}
              schedules={schedules.data ?? []}
              maintenance={maintenanceWindows.data ?? []}
              scheduleEditsAllowed={scheduleEditsAllowed}
              scheduleReadFailed={occurrencesFailed}
              onOpenTask={openLaneTask}
              onCreateMaintenance={
                maintenanceEditsAllowed
                  ? (range) =>
                      createMaintenance.mutate({
                        starts_at_ms: range.startMs,
                        ends_at_ms: range.endMs,
                        ...(range.reason ? { reason: range.reason } : {}),
                      })
                  : undefined
              }
              liftRulesAllowed={maintenanceEditsAllowed}
              blockPreset={blockPreset}
              tabBar={phoneTabs}
              notices={
                <>
                  {dispatchBanner}
                  {calendarBanners}
                </>
              }
            />
          ) : urlState.cal === 'month' ? (
            <TaskCalendar
              siteId={site.id}
              dateKey={calDateKey}
              timeZone={siteZone.timeZone}
              tasks={rows}
              occurrences={occurrenceRows}
              schedules={schedules.data ?? []}
              maintenance={maintenanceWindows.data ?? []}
              maintenanceFailed={maintenanceWindows.isError && !maintenanceWindows.data}
              onNavigate={(patch) =>
                navigateCalendar(
                  {
                    ...(patch.cal !== undefined ? { cal: patch.cal } : {}),
                    ...(patch.date !== undefined ? { date: patch.date } : {}),
                  },
                  // Paging months replaces the entry, as it always did;
                  // opening a day is a step Back undoes.
                  { replace: patch.cal === undefined },
                )
              }
              onNewTask={dispatchAllowed ? openNewTaskFromCalendar : undefined}
              onNewTaskOnDay={dispatchAllowed ? openNewTaskOnDay : undefined}
            />
          ) : (
            <CalendarLanesContainer
              siteId={site.id}
              view={urlState.cal}
              dateKey={calDateKey}
              followsToday={urlState.date === null}
              timeZone={siteZone.timeZone}
              timeZoneSource={siteZone.source}
              window={lanesWindow!}
              agendaDays={urlState.days}
              scheduleEditsAllowed={scheduleEditsAllowed}
              savedSchedule={savedSchedule}
              tasks={rows}
              occurrences={occurrenceRows}
              schedules={schedules.data ?? []}
              maintenance={maintenanceWindows.data ?? []}
              robotFilter={urlState.robot}
              fullDay={urlState.fullDay}
              onNavigate={(patch) =>
                navigateCalendar(
                  {
                    ...(patch.cal !== undefined ? { cal: patch.cal } : {}),
                    ...(patch.date !== undefined ? { date: patch.date } : {}),
                    ...(patch.robot !== undefined ? { robot: patch.robot } : {}),
                    ...(patch.fullDay !== undefined ? { fullDay: patch.fullDay } : {}),
                    ...(patch.days !== undefined ? { days: patch.days } : {}),
                  },
                  // Typing in "Find robot" must not stack a history entry
                  // per keystroke; paging and drilling in should.
                  { replace: patch.robot !== undefined && patch.cal === undefined },
                )
              }
              onOpenTask={openLaneTask}
              onSkipOccurrence={
                scheduleEditsAllowed
                  ? (item: LaneItem) =>
                      item.occurrence && setPendingEdit({ mode: 'delete', occurrence: item.occurrence })
                  : undefined
              }
              onMoveOccurrence={
                scheduleEditsAllowed
                  ? (item: LaneItem, newStartMs: number) =>
                      item.occurrence &&
                      setPendingEdit({ mode: 'move', occurrence: item.occurrence, newStartMs })
                  : undefined
              }
              onPauseSeries={
                scheduleEditsAllowed
                  ? (scheduleId: string, alsoIds?: readonly string[]) =>
                      toggleFromPanel([scheduleId, ...(alsoIds ?? [])], false)
                  : undefined
              }
              onEditSchedule={onEditSchedule}
              onCreateAtSlot={handleSlot}
              onCreateMaintenance={
                maintenanceEditsAllowed
                  ? (range) =>
                      createMaintenance.mutate({
                        starts_at_ms: range.startMs,
                        ends_at_ms: range.endMs,
                        ...(range.reason ? { reason: range.reason } : {}),
                      })
                  : undefined
              }
              onDeleteMaintenance={
                maintenanceEditsAllowed
                  ? // Answers whether it took, so the lane panel can close
                    // on a removal (BB-B-3); never rejects -- a refusal is
                    // the error line above, from the mutation's own state.
                    (w) =>
                      deleteMaintenance.mutateAsync(w.window_id).then(
                        () => true,
                        () => false,
                      )
                  : undefined
              }
              liftRulesAllowed={maintenanceEditsAllowed}
              blockPreset={blockPreset}
              onNewTask={dispatchAllowed ? openNewTaskFromCalendar : undefined}
              renderTaskActions={renderTaskActions}
              editGroupsHref="/site-settings#robot-groups"
            />
          )}
        </>
      )}

      <OccurrenceScopeDialog
        open={pendingEdit !== null}
        mode={pendingEdit?.mode ?? 'move'}
        single={pendingIsSingle()}
        title={pendingEdit?.occurrence.name ?? ''}
        detail={pendingEditDetail()}
        seriesNote={pendingEdit?.mode === 'move' ? followingRunsNote() : undefined}
        seriesStop={pendingEdit?.mode === 'move' ? followingRunsStop() : undefined}
        seriesChecking={pendingEdit?.mode === 'move' && followingChecking}
        seriesError={
          pendingEdit?.mode === 'move' && followingFailed
            ? followingPreview.error instanceof Error
              ? followingPreview.error.message
              : 'the server did not answer.'
            : null
        }
        plan={pendingEdit?.mode === 'delete' ? planChoice() : undefined}
        pending={scheduleEditPending}
        error={
          planNote && planNote.from !== null && planNote.from === pendingEdit
            ? planNote.text
            : scheduleMutationError
              ? isFireModeError(scheduleMutationError)
                ? FIRE_MODE_HOLD_COPY
                : scheduleMutationError.message
              : null
        }
        onChoose={applyScope}
        onCancel={closeScopeDialog}
      />

      {/* "+ New task". Mounted while dispatch is allowed, on either tab, so
          a half-written task survives closing the drawer and a trip to the
          calendar; drawn only on the table tab, where its button is --
          calendar users create through the empty-slot modal below. */}
      <RoleGuard allowedRoles={[...TASK_DISPATCH_ROLES]}>
        {!writeDisabled ? (
          <NewTaskDrawer open={formOpen} onClose={closeForm} triggerRef={drawerTriggerRef}>
            <TaskDispatchForm onSuccess={() => tasks.refetch()} prefillStart={drawerPrefill} />
          </NewTaskDrawer>
        ) : null}
      </RoleGuard>

      {/* The schedule editor, mounted from the first Edit until a save,
          another schedule's Edit, or an Edit on a form holding no
          change; hidden in between. */}
      {editing && (
        <ScheduleEditDrawer
          key={editing.key}
          open={editOpen}
          schedule={
            (schedules.data ?? []).find((x) => x.schedule_id === editing.scheduleId) ?? null
          }
          schedules={schedules.data}
          siteTimeZone={siteZone.timeZone}
          applyFromMs={editing.applyFromMs}
          onClose={closeScheduleEditor}
          onSaved={onScheduleSaved}
          onDirtyChange={onEditDirty}
          onReload={reloadScheduleEditor}
          triggerRef={editTriggerRef}
        />
      )}
      {savedNote && <ActionToast message={savedNote} onDone={clearSavedNote} />}

      {selected && <TaskDetailDrawer task={selected} onClose={() => setSelectedId(null)} />}
      {selectedOlder && (
        <OlderTaskDrawer task={selectedOlder} onClose={() => setSelectedOlderId(null)} />
      )}
      {calendarOlder && !selectedOlder && (
        <OlderTaskDrawer task={calendarOlder} onClose={() => setCalendarOlder(null)} />
      )}

      {/* Calendar-driven create modal, opened by clicking an empty
          slot. (Dragging an EXISTING block no longer routes here: a
          dispatched task cannot be rescheduled at all, and a scheduled
          occurrence goes through the this-event/all-events dialog
          above.) The role check is
          applied directly to the modal mount so the TaskModal's
          `createPortal(..., document.body)` call is unconditional and
          the backdrop mounts/cleans up atomically — wrapping the
          modal in <RoleGuard> caused the backdrop to be left mounted
          in some render timings and fall behind the calendar's
          sticky toolbar (z-index stacking-context bug). The modal
          itself uses `z-index: 1000` so it sits above the TopBar. */}
      {createSlot && createModalOpen && !writeDisabled && roleAllowed && (
        <TaskModal
          open
          title={`New task at ${formatDateTime(createSlot.start)}`}
          submitLabel="Dispatch"
          cancelLabel="Cancel"
          onCancel={() => {
            setCreateSlot(null);
            setCreateModalOpen(false);
          }}
        >
          <TaskDispatchForm
            prefillStart={createSlot.start}
            onSuccess={() => {
              setCreateSlot(null);
              setCreateModalOpen(false);
              void tasks.refetch();
            }}
          />
        </TaskModal>
      )}
    </div>
  );
}

/** Today and the time on the SITE's clock, beside the phone's title.
 *  Its own component so the once-a-minute tick re-renders only this. */
function TasksPhoneClock({ timeZone }: { timeZone: string }) {
  const nowMs = useMinuteNow();
  return (
    <span className="nx-tasks__clock" data-testid="tasks-phone-clock">
      {phoneDateLine(nowMs, timeZone)}
    </span>
  );
}
